import React, { useState } from 'react';
import { useAuth } from '@/auth/clerkShim';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Check, PlayCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import useTourStore, { TourId } from '@/store/tourStore';
import useAdminStore from '@/store/adminStore';
import { TOURS, getTourById } from '@/tours';

interface TourLauncherProps {
  variant?: 'icon' | 'button';
  className?: string;
}

export default function TourLauncher({ variant = 'icon', className = '' }: TourLauncherProps) {
  const [open, setOpen] = useState(false);
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { completedTours, toursDismissed, markCompleted, dismissTours, hasCompleted } = useTourStore();
  const admin = useAdminStore((state: any) => state.admin);
  const clerkId = admin?.provider?.id;

  const handleStartTour = (tourId: TourId) => {
    setOpen(false);
    const tour = getTourById(tourId);
    if (tour && clerkId) {
      setTimeout(() => {
        tour.startTour(() => {
          markCompleted(tourId, clerkId, getToken);
        }, navigate);
      }, 300);
    }
  };

  const handleDismissAll = () => {
    if (clerkId) {
      dismissTours(clerkId, getToken);
    }
    setOpen(false);
  };

  const completedCount = completedTours.length;
  const totalCount = TOURS.length;
  const allCompleted = completedCount === totalCount;

  if (toursDismissed) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {variant === 'icon' ? (
          <button
            className={`relative flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-muted-foreground transition-all hover:bg-border hover:text-foreground ${className}`}
            aria-label="Help & Tours"
          >
            <HelpCircle className="h-5 w-5" />
            {!allCompleted && (
              <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                {totalCount - completedCount}
              </span>
            )}
          </button>
        ) : (
          <Button variant="outline" className={className}>
            <HelpCircle className="mr-2 h-4 w-4" />
            Getting Started
            {!allCompleted && (
              <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-white">
                {completedCount}/{totalCount}
              </span>
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="border-b border-border bg-gradient-to-r from-muted/50 to-card px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">Getting Started</h3>
              <p className="text-sm text-muted-foreground">
                {allCompleted ? 'All tours completed!' : `${completedCount} of ${totalCount} completed`}
              </p>
            </div>
            {!allCompleted && (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <span className="text-sm font-bold text-primary">
                  {Math.round((completedCount / totalCount) * 100)}%
                </span>
              </div>
            )}
          </div>
          <Progress
            value={(completedCount / totalCount) * 100}
            className="mt-3 h-1.5 bg-muted"
            indicatorClassName="rounded-full bg-gradient-to-r from-primary to-primary-deep duration-500"
          />
        </div>

        <div className="max-h-[320px] overflow-y-auto p-2">
          {TOURS.map((tour) => {
            const isCompleted = hasCompleted(tour.id);
            return (
              <button
                key={tour.id}
                onClick={() => handleStartTour(tour.id)}
                className={`group flex w-full items-start gap-3 rounded-xl p-3 text-left transition-all ${
                  isCompleted
                    ? 'bg-success/10 hover:bg-success/20'
                    : 'hover:bg-muted/50'
                }`}
              >
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
                    isCompleted
                      ? 'bg-success/20'
                      : 'bg-muted group-hover:bg-primary/10'
                  }`}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5 text-success" />
                  ) : (
                    <tour.icon className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-medium ${
                        isCompleted ? 'text-success' : 'text-foreground'
                      }`}
                    >
                      {tour.name}
                    </span>
                    {isCompleted && (
                      <span className="rounded-full bg-success/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-success">
                        Done
                      </span>
                    )}
                  </div>
                  <p
                    className={`mt-0.5 text-sm ${
                      isCompleted ? 'text-success' : 'text-muted-foreground'
                    }`}
                  >
                    {tour.description}
                  </p>
                </div>
                {!isCompleted && (
                  <PlayCircle className="h-5 w-5 flex-shrink-0 text-muted-foreground/60 group-hover:text-primary" />
                )}
              </button>
            );
          })}
        </div>

        <div className="border-t border-border p-2">
          <button
            onClick={handleDismissAll}
            className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          >
            <X className="h-4 w-4" />
            Don't show tours again
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
